<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ArrowRight, RefreshCw } from '@lucide/vue'
import { Badge, Button, PageHeader, Section, Spinner, Table } from '@drama/ui'

import { listVideoPreparation } from '../../api'
import { selectScenario } from './scenario.js'

const { locale, t } = useI18n()
const router = useRouter()
const loading = ref(false)
const error = ref('')
const items = ref([])

const columns = computed(() => [
  { key: 'scenario', label: t('visualGameAdmin.intentVideo.preparation.scenario'), width: '240px' },
  { key: 'action', label: t('visualGameAdmin.intentVideo.preparation.action'), width: '300px' },
  { key: 'player', label: t('visualGameAdmin.intentVideo.preparation.player'), width: '230px' },
  { key: 'npc', label: t('visualGameAdmin.intentVideo.preparation.npc'), width: '230px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '230px', align: 'right', fixed: 'right' },
])

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    items.value = (await listVideoPreparation(locale.value)).items
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function clusterVariant(status) {
  switch (status) {
    case 'ready': return 'success'
    case 'stale': return 'warning'
    case 'not_analyzed': return 'neutral'
    case 'no_history': return 'neutral'
    default: throw new Error(`Unhandled cluster preparation status: ${status}`)
  }
}

function talkingVariant(talking) {
  if (talking.specs === 0) return 'neutral'
  if (talkingReady(talking)) return 'success'
  return 'warning'
}

function talkingLabelKey(talking) {
  if (talking.specs === 0) return 'visualGameAdmin.intentVideo.preparation.notConfigured'
  if (talkingReady(talking)) return 'visualGameAdmin.intentVideo.preparation.ready'
  return 'visualGameAdmin.intentVideo.preparation.inProgress'
}

function talkingReady(talking) {
  return talking.approved_specs === talking.specs
    && talking.state_frames === talking.specs
    && talking.videos === talking.specs
}

function openClusters(row) {
  selectScenario(row.scenario_id)
  router.push({ name: 'intent-video-clusters', query: { scenario_id: row.scenario_id } })
}

function openTalking(row) {
  router.push({ name: 'asset-library', query: { pack_id: row.scenario_id } })
}
</script>

<template>
  <div class="ivp vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/PreparationPage.vue">
    <PageHeader :title="t('visualGameAdmin.intentVideo.tabs.preparation')">
      <template #subtitle>{{ t('visualGameAdmin.intentVideo.preparation.subtitle') }}</template>
      <template #actions>
        <Button variant="secondary" size="sm" :loading="loading" @click="load">
          <RefreshCw :size="14" />{{ t('visualGameAdmin.common.refresh') }}
        </Button>
      </template>
    </PageHeader>

    <Section :padded="false">
      <div v-if="loading" class="vga-state"><Spinner /></div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <Table v-else :columns="columns" :rows="items" row-key="scenario_id">
        <template #scenario="{ row }">
          <div class="ivp__scenario">
            <img v-if="row.preview_url !== null" :src="row.preview_url" :alt="row.display_title">
            <div>
              <strong>{{ row.display_title }}</strong>
              <span v-if="row.series_title !== null">{{ row.series_title }} · {{ row.scenario_id }}</span>
              <span v-else>{{ row.scenario_id }}</span>
            </div>
          </div>
        </template>
        <template #action="{ row }">
          <div class="ivp__status">
            <Badge :variant="clusterVariant(row.action.status)" size="sm">
              {{ t(`visualGameAdmin.intentVideo.preparation.clusterStatus.${row.action.status}`) }}
            </Badge>
            <span>{{ t('visualGameAdmin.intentVideo.preparation.actionCounts', {
              turns: row.action.historical_turns,
              clusters: row.action.clusters,
              target: row.action.target_videos,
              videos: row.action.playable_resources,
            }) }}</span>
            <span>{{ t('visualGameAdmin.intentVideo.preparation.coverage', { rate: (row.action.coverage_rate * 100).toFixed(1) }) }}</span>
          </div>
        </template>
        <template #player="{ row }">
          <div class="ivp__status">
            <Badge :variant="talkingVariant(row.talking.player)" size="sm">
              {{ t(talkingLabelKey(row.talking.player)) }}
            </Badge>
            <span>{{ t('visualGameAdmin.intentVideo.preparation.talkingCounts', row.talking.player) }}</span>
          </div>
        </template>
        <template #npc="{ row }">
          <div class="ivp__status">
            <Badge :variant="talkingVariant(row.talking.npc)" size="sm">
              {{ t(talkingLabelKey(row.talking.npc)) }}
            </Badge>
            <span>{{ t('visualGameAdmin.intentVideo.preparation.talkingCounts', row.talking.npc) }}</span>
          </div>
        </template>
        <template #actions="{ row }">
          <div class="ivp__actions">
            <Button variant="ghost" size="sm" @click="openClusters(row)">
              {{ t('visualGameAdmin.intentVideo.tabs.clusters') }}<ArrowRight :size="14" />
            </Button>
            <Button variant="ghost" size="sm" @click="openTalking(row)">
              {{ t('visualGameAdmin.assetLibrary.tabs.talkingVideos') }}<ArrowRight :size="14" />
            </Button>
          </div>
        </template>
      </Table>
    </Section>
  </div>
</template>

<style scoped>
.ivp__status {
  display: grid;
  gap: var(--drama-space-xs);
}

.ivp__scenario {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  align-items: center;
  gap: var(--drama-space-md);
}

.ivp__scenario img {
  display: block;
  width: 88px;
  aspect-ratio: 16 / 9;
  border-radius: var(--drama-radius-sm);
  object-fit: cover;
}

.ivp__scenario div {
  display: grid;
  gap: var(--drama-space-xs);
  min-width: 0;
}

.ivp__scenario span,
.ivp__status span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.ivp__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--drama-space-xs);
}
</style>
